import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const plan = sp.get("plan");
  const PLANS = {
    basic: { name: "Basic", blurb: "Launch a single self-funded agent with policy guardrails and a hard-stop wallet." },
    pro: { name: "Pro", blurb: "Run a fleet of revenue-earning agents with autonomous marketing, prospecting and deployments." },
    org: { name: "Org", blurb: "Multi-tenant control, team seats and org-wide spend policy for scaled agent operations." },
  };
  const planInfo = plan && PLANS[plan.toLowerCase()];
  useSeo({
    title: planInfo ? `Sign in — Kleen-G ${planInfo.name} plan` : "Sign in or create your workspace — Kleen-G",
    description: planInfo
      ? `Access your Kleen-G ${planInfo.name} workspace. ${planInfo.blurb}`
      : "Sign in to Kleen-G or create a new workspace to deploy self-funded autonomous AI agents that earn revenue and spend under strict policy.",
    path: "/login",
  });
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ email: "", password: "", name: "", workspace_name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        setSent(true);
        toast.success("Reset link sent if the account exists");
        return;
      }
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      login(data);
      toast.success(mode === "login" ? "Welcome back" : "Workspace provisioned");
      navigate(plan ? `/billing?plan=${plan}` : "/console");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(""); setSent(false); };

  return (
    <div className="min-h-screen bg-[#080B11] flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <img src="/kleeng-logo.png" alt="Kleen-G" className="w-10 h-10 rounded-md" />
          <div className="text-center">
            <div className="font-display font-black text-xl tracking-tight leading-none">Kleen-G</div>
            <div className="font-mono text-[9px] text-slate-500 tracking-[0.15em] uppercase mt-0.5">Self-Funded Autonomous Agents</div>
          </div>
        </div>

        <div className="hud-card accent-top rounded-xl p-8" style={{ "--accent-line": "#06B6D4" }}>
          <div className="mb-6 text-center">
            <h1 className="font-display text-xl font-bold text-slate-100">
              {planInfo ? `Sign in to the ${planInfo.name} plan` : "Sign in to Kleen-G"}
            </h1>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              {planInfo
                ? planInfo.blurb
                : "Deploy self-funded autonomous AI agents that earn revenue, spend under policy, and hard-stop on breach."}
              {" "}Sign in to your workspace or create a new one to launch agents, connect payments, run marketing and prospecting, and ship client sites — all from one control room.
            </p>
          </div>

          {mode !== "forgot" && (
            <div className="flex gap-2 mb-6 p-1 rounded-md bg-[#080B11] border border-slate-800">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  data-testid={`tab-${m}`}
                  onClick={() => switchMode(m)}
                  className={`flex-1 py-2 rounded font-mono text-[11px] uppercase tracking-wider font-semibold transition-colors ${
                    mode === m ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30" : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {m === "login" ? "Sign In" : "New Workspace"}
                </button>
              ))}
            </div>
          )}

          {mode === "forgot" && sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <MailCheck className="w-12 h-12 text-emerald-400 mx-auto" />
              <h1 className="mt-4 font-display text-lg font-bold">Check your inbox</h1>
              <p className="mt-2 text-sm text-slate-400">If an account exists for <span className="text-slate-200">{form.email}</span>, we've sent a password reset link. It expires in 1 hour.</p>
              <button data-testid="back-to-login-btn" onClick={() => switchMode("login")} className="mt-6 text-cyan-400 text-sm font-mono hover:text-cyan-300 inline-flex items-center gap-1.5">
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {mode === "forgot" && (
                <div className="mb-2">
                  <h1 className="font-display text-lg font-bold">Reset your password</h1>
                  <p className="text-xs text-slate-500 mt-1">Enter your account email and we'll send a secure reset link.</p>
                </div>
              )}
              {mode === "register" && (
                <>
                  <Field label="Full Name" testid="input-name" value={form.name} onChange={set("name")} required />
                  <Field label="Workspace Name" testid="input-workspace" value={form.workspace_name} onChange={set("workspace_name")} required placeholder="Apex Capital Operations" />
                </>
              )}
              <Field label="Email" type="email" testid="input-email" value={form.email} onChange={set("email")} required />
              {mode !== "forgot" && (
                <Field label="Password" type="password" testid="input-password" value={form.password} onChange={set("password")} required />
              )}

              {mode === "login" && (
                <div className="text-right -mt-1">
                  <button type="button" data-testid="forgot-password-link" onClick={() => switchMode("forgot")} className="text-[11px] font-mono text-cyan-400/80 hover:text-cyan-300 transition-colors">
                    Forgot password?
                  </button>
                </div>
              )}

              {error && <div data-testid="auth-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{error}</div>}

              <button
                type="submit"
                data-testid={mode === "forgot" ? "forgot-submit-btn" : "auth-submit-btn"}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-md bg-cyan-500 text-[#080B11] font-bold hover:bg-cyan-400 transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    {mode === "login" && "Enter Console"}
                    {mode === "register" && "Provision Workspace"}
                    {mode === "forgot" && "Send Reset Link"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {mode === "forgot" && (
                <button type="button" data-testid="forgot-cancel-btn" onClick={() => switchMode("login")} className="w-full text-center text-[11px] font-mono text-slate-500 hover:text-slate-300 transition-colors">
                  Back to sign in
                </button>
              )}
            </form>
          )}

          {mode === "login" && (
            <div className="mt-5 text-center text-[11px] font-mono text-slate-500">
              Secure session · protected by refresh-token rotation
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="font-mono text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-400 mb-1.5 block">{label}</label>
      <input
        data-testid={testid}
        {...props}
        className="w-full px-3 py-2.5 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60 transition-colors"
      />
    </div>
  );
}
